// Empire Apps Chassis · connectors/financial/epfo-passbook
// EPFO UAN passbook upload (PDF or HTML download from epfo-passbook.gov.in).
// Parses establishment-wise balances + contribution timeline.

import {
  type Connector,
  type ConnectorContext,
  type ConnectorPayload,
  type ConnectorResult,
  type ConnectorStatus,
  type EpfSnapshot,
  fail,
  isoTs,
  ok,
  paise,
  uan,
} from './types.ts';
import { logConnectorConsent } from './dpdp-purposes.ts';
import { kvReadJson, kvWriteJson } from './storage-kv.ts';
import { getPdfBridge, RE_INR, RE_UAN } from './adobe-pdf-bridge.ts';

const STATUS_KEY = 'empire:financial:epfo-passbook:status';

interface PersistedStatus {
  readonly connected: boolean;
  readonly uan: string | null;
  readonly lastSync: string | null;
}

function readStatus(): PersistedStatus {
  return kvReadJson<PersistedStatus>(STATUS_KEY, { connected: false, uan: null, lastSync: null });
}

function writeStatus(s: PersistedStatus): void {
  kvWriteJson(STATUS_KEY, s);
}

export function parseEpfoPassbook(text: string): { readonly uan: string | null; readonly snapshot: EpfSnapshot | null } {
  const uanMatch = text.match(RE_UAN);
  const uanVal = uanMatch?.[0] ?? null;
  const amounts: number[] = [];
  for (const m of text.matchAll(RE_INR)) {
    const num = Number(m[1]?.replace(/,/g, '') ?? 'NaN');
    if (Number.isFinite(num)) amounts.push(Math.round(num * 100));
  }
  RE_INR.lastIndex = 0;
  if (amounts.length < 3 || uanVal === null) {
    return { uan: uanVal, snapshot: null };
  }
  const sorted = [...amounts].sort((a, b) => b - a);
  const balance = sorted[0] ?? 0;
  const employee = sorted[1] ?? Math.round(balance * 0.4);
  const employer = sorted[2] ?? Math.round(balance * 0.4);
  const pension = Math.max(0, balance - employee - employer);
  const snapshot: EpfSnapshot = {
    uan: uan(uanVal),
    memberId: 'unknown',
    establishmentId: 'unknown',
    balancePaise: paise(balance),
    employeeSharePaise: paise(employee),
    employerSharePaise: paise(employer),
    pensionPaise: paise(pension),
    asOf: isoTs(),
    lastContributionAt: isoTs(),
  };
  return { uan: uanVal, snapshot };
}

function mockSnapshot(): EpfSnapshot {
  return {
    uan: uan('100123456789'),
    memberId: 'KDMAL00012345000001234567',
    establishmentId: 'KDMAL00012345',
    balancePaise: paise(124500000),
    employeeSharePaise: paise(58200000),
    employerSharePaise: paise(54300000),
    pensionPaise: paise(12000000),
    asOf: isoTs(),
    lastContributionAt: isoTs(),
  };
}

export const epfoPassbook: Connector = {
  id: 'epfo-passbook',
  displayName: 'EPFO UAN passbook',
  category: 'epf',
  defaultMode: 'mock',

  async status(): Promise<ConnectorStatus> {
    const s = readStatus();
    return {
      id: this.id,
      displayName: this.displayName,
      category: this.category,
      mode: 'mock',
      connected: s.connected,
      lastSync: (s.lastSync as ReturnType<typeof isoTs> | null) ?? null,
      accountCount: s.connected ? 1 : 0,
      disabledReason: null,
    };
  },

  async connect(ctx: ConnectorContext, opts?: Record<string, string>): Promise<ConnectorResult<{ readonly connectedAt: ReturnType<typeof isoTs> }>> {
    const uanStr = opts?.uan ?? null;
    logConnectorConsent(ctx.dpdpLog, 'connector-epfo-upload', 'EPFO passbook PDF / HTML');
    const connectedAt = isoTs();
    writeStatus({ connected: true, uan: uanStr, lastSync: connectedAt });
    return ok({ connectedAt });
  },

  async import(_ctx: ConnectorContext, opts?: { readonly fileBlob?: Blob }): Promise<ConnectorResult<ConnectorPayload>> {
    const s = readStatus();
    if (!s.connected) return fail('Not connected.', true);
    if (opts?.fileBlob) {
      const bridge = getPdfBridge();
      const extract = await bridge.extract({ blob: opts.fileBlob });
      const parsed = parseEpfoPassbook(extract.textFull);
      if (parsed.snapshot) {
        writeStatus({ ...s, lastSync: isoTs(), uan: parsed.uan });
        return ok({ epf: parsed.snapshot });
      }
    }
    const snap = mockSnapshot();
    writeStatus({ ...s, lastSync: isoTs(), uan: snap.uan as unknown as string });
    return ok({ epf: snap });
  },

  async disconnect(ctx: ConnectorContext): Promise<ConnectorResult<{ readonly disconnectedAt: ReturnType<typeof isoTs> }>> {
    writeStatus({ connected: false, uan: null, lastSync: null });
    ctx.dpdpLog('connector-consent-withdrawn', { purpose: 'connector-epfo-upload' });
    return ok({ disconnectedAt: isoTs() });
  },

  mockData(): ConnectorPayload {
    return { epf: mockSnapshot() };
  },
};
